import React, { useState } from 'react'
import { Link, useLocation } from 'react-router-dom'
import { motion } from 'framer-motion'
import * as FiIcons from 'react-icons/fi'
import SafeIcon from '../common/SafeIcon'
import Tooltip from './onboarding/Tooltip'
import { useAuth } from './auth/AuthProvider'

const { FiTarget, FiHome, FiList, FiBarChart3, FiSettings, FiMenu, FiX, FiZap, FiBookOpen, FiBell, FiEye, FiLogOut, FiUser, FiShoppingBag } = FiIcons

const Navbar = () => {
  const [isOpen, setIsOpen] = useState(false)
  const location = useLocation()
  const { user, logout } = useAuth()

  const navItems = [
    { name: 'Dashboard', path: '/', icon: FiHome, description: 'Command Center', tourId: 'dashboard' },
    { name: 'Habits', path: '/habits', icon: FiList, description: 'Build Power', tourId: 'habits' },
    { name: 'Vision', path: '/vision', icon: FiEye, description: 'Forge Dreams', tourId: 'vision' },
    { name: 'Analytics', path: '/analytics', icon: FiBarChart3, description: 'Track Dominance', tourId: 'analytics' },
    { name: 'Products', path: '/products', icon: FiShoppingBag, description: 'Recommendations', tourId: 'products' },
    { name: 'Reflections', path: '/reflections', icon: FiBookOpen, description: 'Mind Forge', tourId: 'reflections' },
    { name: 'Reminders', path: '/reminders', icon: FiBell, description: 'Never Miss', tourId: 'reminders' },
    { name: 'Settings', path: '/settings', icon: FiSettings, description: 'Control Panel', tourId: 'settings' }
  ]

  const isActive = (path) => location.pathname === path

  const handleLogout = () => {
    logout()
    setIsOpen(false)
  }

  return (
    <nav className="glass-effect border-b border-dreamforge-yellow/20 sticky top-0 z-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16">
          {/* Logo */}
          <Link to="/" className="flex items-center space-x-3 group">
            <div className="relative">
              <div className="w-10 h-10 bg-forge-gradient rounded-2xl flex items-center justify-center shadow-forge group-hover:shadow-lg transition-all duration-300 group-hover:animate-forge">
                <SafeIcon icon={FiTarget} className="text-dark-950 text-lg font-bold" />
              </div>
              <div className="absolute -top-1 -right-1 w-4 h-4 bg-dreamforge-red rounded-full flex items-center justify-center animate-pulse">
                <SafeIcon icon={FiZap} className="text-white text-xs" />
              </div>
            </div>
            <div>
              <span className="text-xl font-bold text-forge bg-forge-gradient bg-clip-text text-transparent">
                DreamForge
              </span>
              <div className="text-xs text-dreamforge-yellow/80 -mt-1">Forge Your Future</div>
            </div>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center space-x-1">
            {navItems.map((item) => (
              <Tooltip
                key={item.name}
                content={`${item.description} - ${
                  item.name === 'Dashboard' ? 'Your daily command center' :
                  item.name === 'Habits' ? 'Create and track habits' :
                  item.name === 'Vision' ? 'Visualize your goals' :
                  item.name === 'Analytics' ? 'Performance insights' :
                  item.name === 'Products' ? 'Curated recommendations' :
                  item.name === 'Reflections' ? 'Journal your journey' :
                  item.name === 'Reminders' ? 'Stay on track' :
                  'Customize settings'
                }`}
                title={item.name}
                position="bottom"
                variant="info"
                showDelay={800}
              >
                <Link
                  to={item.path}
                  data-tour={item.tourId}
                  className={`relative flex items-center space-x-2 px-3 py-2 rounded-2xl transition-all duration-300 group ${
                    isActive(item.path)
                      ? 'bg-forge-gradient text-dark-950 shadow-forge font-semibold'
                      : 'text-dreamforge-yellow/80 hover:text-dreamforge-yellow hover:bg-dreamforge-lightblue/20'
                  }`}
                >
                  <SafeIcon icon={item.icon} className="text-lg" />
                  <div className="flex flex-col">
                    <span className="font-medium text-sm">{item.name}</span>
                    <span className={`text-xs ${
                      isActive(item.path) ? 'text-dark-950/70' : 'text-dreamforge-yellow/60'
                    }`}>
                      {item.description}
                    </span>
                  </div>
                  {isActive(item.path) && (
                    <motion.div
                      layoutId="activeTab"
                      className="absolute inset-0 bg-forge-gradient rounded-2xl -z-10"
                      initial={false}
                      transition={{ type: "spring", stiffness: 500, damping: 30 }}
                    />
                  )}
                </Link>
              </Tooltip>
            ))}
          </div>

          {/* User Menu & Mobile Toggle */}
          <div className="flex items-center space-x-3">
            {/* User Info */}
            {user && (
              <Tooltip
                content={`Logged in as ${user.name}`}
                title="User Profile"
                position="bottom"
                variant="success"
              >
                <div className="hidden sm:flex items-center space-x-2 bg-dreamforge-lightblue/20 px-3 py-1 rounded-xl">
                  <div className="w-6 h-6 bg-forge-gradient rounded-full flex items-center justify-center">
                    <SafeIcon icon={FiUser} className="text-dark-950 text-xs" />
                  </div>
                  <span className="text-dreamforge-yellow text-sm font-medium">{user.name}</span>
                </div>
              </Tooltip>
            )}

            {/* Logout Button */}
            {user && (
              <Tooltip
                content="Sign out of your account"
                title="Logout"
                position="bottom"
                variant="warning"
              >
                <button
                  onClick={handleLogout}
                  className="hidden sm:flex p-2 text-dreamforge-yellow hover:text-dreamforge-red hover:bg-dreamforge-lightblue/20 rounded-xl transition-all duration-300"
                >
                  <SafeIcon icon={FiLogOut} className="text-lg" />
                </button>
              </Tooltip>
            )}

            {/* Mobile menu button */}
            <button
              onClick={() => setIsOpen(!isOpen)}
              className="lg:hidden p-2 rounded-2xl hover:bg-dreamforge-lightblue/20 transition-colors"
            >
              <SafeIcon icon={isOpen ? FiX : FiMenu} className="text-dreamforge-yellow text-xl" />
            </button>
          </div>
        </div>

        {/* Mobile Navigation */}
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            className="lg:hidden py-4 border-t border-dreamforge-yellow/20"
          >
            {navItems.map((item) => (
              <Link
                key={item.name}
                to={item.path}
                onClick={() => setIsOpen(false)}
                className={`flex items-center space-x-3 px-4 py-3 rounded-2xl transition-all duration-300 mb-2 ${
                  isActive(item.path)
                    ? 'bg-forge-gradient text-dark-950 shadow-forge font-semibold'
                    : 'text-dreamforge-yellow/80 hover:text-dreamforge-yellow hover:bg-dreamforge-lightblue/20'
                }`}
              >
                <SafeIcon icon={item.icon} className="text-lg" />
                <div className="flex flex-col">
                  <span className="font-medium">{item.name}</span>
                  <span className={`text-xs ${
                    isActive(item.path) ? 'text-dark-950/70' : 'text-dreamforge-yellow/60'
                  }`}>
                    {item.description}
                  </span>
                </div>
              </Link>
            ))}

            {/* Mobile User Actions */}
            {user && (
              <div className="mt-4 pt-4 border-t border-dreamforge-yellow/20">
                <div className="flex items-center space-x-3 px-4 py-2 mb-2">
                  <div className="w-8 h-8 bg-forge-gradient rounded-full flex items-center justify-center">
                    <SafeIcon icon={FiUser} className="text-dark-950" />
                  </div>
                  <div>
                    <div className="text-dreamforge-yellow font-medium">{user.name}</div>
                    <div className="text-dreamforge-yellow/60 text-sm">{user.email}</div>
                  </div>
                </div>
                <button
                  onClick={handleLogout}
                  className="flex items-center space-x-3 px-4 py-3 text-dreamforge-red hover:bg-dreamforge-lightblue/20 rounded-2xl transition-colors w-full"
                >
                  <SafeIcon icon={FiLogOut} className="text-lg" />
                  <span>Sign Out</span>
                </button>
              </div>
            )}
          </motion.div>
        )}
      </div>
    </nav>
  )
}

export default Navbar